// Centralized API wrapper for the AI-QA-Engine backend.
// - attaches the Bearer token from localStorage
// - normalizes errors into ApiError
// - on 401 clears the token and broadcasts "aiqa:unauthorized" so AuthContext
//   can drop the session.
//
// Base URL resolution (evaluated per request so it can change at runtime):
//   1. window.__AIQA_API_BASE__  (deployed build)
//   2. localStorage "aiqa-api-base"  (the Chrome extension sets this to an
//      absolute backend URL — it has no dev proxy)
//   3. "/api"  → same-origin → the SPA dev-server proxy rewrites /api → backend
//      (a dedicated prefix so API paths never collide with SPA routes)
const TOKEN_KEY = "aiqa-token";
const BASE_KEY = "aiqa-api-base";

// Request tracing is a development aid. It used to run unconditionally, so a
// production build logged the full URL of every request the app ever made into
// the user's console. webpack's `mode` substitutes process.env.NODE_ENV at build
// time, so in a production bundle DEBUG folds to `false` and the minifier drops
// every call below as dead code.
// Guard at each CALL SITE (`DEBUG && …`), not inside a helper: passing the
// arguments to a helper still evaluates the template concatenation on every
// request in production, and leaves the log strings in the shipped bundle.
const DEBUG = process.env.NODE_ENV !== "production";

// Storage can be unavailable (private mode, blocked site data) and none of the
// reads or writes below may throw into a caller. They are NOT put behind DEBUG
// like the request tracing above: that guard exists because tracing runs on
// EVERY request, whereas these fire only when something has actually gone
// wrong, and a silently unsaved token is exactly what you need to see in
// production to explain 'it logged me out again'.
function ignored(what, e) {
  console.debug(`[aiqa/api] ${what} failed (continuing):`, e);
}

export function getApiBase() {
  if (typeof window !== "undefined") {
    if (window.__AIQA_API_BASE__) {
      // eslint-disable-next-line no-console -- dev-only: DEBUG folds to false in a production build and terser drops the call (verified absent from the bundle).
      DEBUG && console.log("[api] using __AIQA_API_BASE__:", window.__AIQA_API_BASE__);
      return window.__AIQA_API_BASE__;
    }
    try {
      const stored = localStorage.getItem(BASE_KEY);
      if (stored) {
        const isLocalOrigin =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";
        const isAbsolute = /^https?:\/\//.test(stored);
        if (isLocalOrigin && isAbsolute) {
          // eslint-disable-next-line no-console -- dev-only: DEBUG folds to false in a production build and terser drops the call (verified absent from the bundle).
          DEBUG && console.log("[api] ignoring stored absolute URL (dev mode):", stored);
          localStorage.removeItem(BASE_KEY);
          return "/api";
        }
        return stored;
      }
    } catch (e) {
      // Falls through to the same-origin "/api" default below, which is right
      // in dev but wrong for a deployed build — worth a line before it happens
      // silently and every request goes to the wrong origin.
      ignored("reading the stored API base", e);
    }
  }
  return "/api";
}

export function setApiBase(value) {
  try {
    if (value) localStorage.setItem(BASE_KEY, value);
    else localStorage.removeItem(BASE_KEY);
  } catch (e) {
    // The backend URL a tester just typed in Settings did not stick. Nothing
    // to show them mid-keystroke, but it explains why it reverts on reload.
    ignored("saving the API base", e);
  }
}

// The field a pydantic/FastAPI validation entry is about.
//
// `loc` is the path to the offending value — ["body", "code"] for a top-level
// field, ["body", "items", 3, "url"] for one item in a list. The leading source
// segment ("body" / "query" / "path") is not a field name, and neither are the
// numeric indices, so both are dropped; what is left is the last NAMED segment,
// which is what a form has an input for.
//
// Only the FIRST segment is the source. It used to be filtered by name wherever
// it appeared, which silently erased any field actually CALLED one of those
// words — ["body", "body"], a changelog entry's description, resolved to null
// and its message could never be attached to the input it was about.
const LOC_SOURCES = ["body", "query", "path", "header", "cookie"];

function fieldOf(entry) {
  const loc = Array.isArray(entry && entry.loc) ? entry.loc : [];
  const path = loc.length && LOC_SOURCES.includes(loc[0]) ? loc.slice(1) : loc;
  const named = path.filter((part) => typeof part === "string");
  return named.length ? named[named.length - 1] : null;
}

function cleanMsg(msg) {
  // pydantic prefixes anything a field_validator raises with "Value error, ".
  // The half after it is the sentence that was written for the user.
  return String(msg || "").replace(/^Value error,\s*/, "");
}

/** Per-field validation messages from a rejected request, as { field: message }.
 *
 * Returns an empty object for any error that isn't a field-level validation
 * failure, so a caller can always spread it into form state unconditionally.
 * Callers use it to put the message ON the offending input instead of showing a
 * single banner that doesn't say which box is wrong.
 */
export function fieldErrors(err) {
  const data = err && err.data;
  if (!data || typeof data !== "object") return {};
  const entries = Array.isArray(data.detail)
    ? data.detail
    : Array.isArray(data.error && data.error.errors)
      ? data.error.errors
      : null;
  if (!entries) return {};
  const out = {};
  entries.forEach((entry) => {
    const field = fieldOf(entry);
    // First message per field wins — a field with two failing rules should read
    // as one problem, not a stack of them.
    if (field && !out[field] && entry && entry.msg) out[field] = cleanMsg(entry.msg);
  });
  return out;
}

// Pull a human-readable message out of whatever error shape the backend sent:
//   - AppException envelope:   { error: { message } }
//   - THIS app's 422 envelope: { error: { type: "VALIDATION_ERROR", errors: [...] } }
//   - FastAPI HTTPException:   { detail: "..." }
//   - FastAPI validation (422): { detail: [{ msg, loc }, ...] }
//   - plain { message } or a bare string
function extractErrorMessage(data, status) {
  if (!data) return `Request failed (${status})`;
  if (typeof data === "string") {
    // A reverse proxy or gateway answers with an HTML error page, not JSON —
    // "<html><body>502 Bad Gateway</body></html>" is markup, not a sentence, and
    // putting it in a toast tells the reader nothing. This is the shape a
    // DEPLOYED app hits (nginx/Traefik in front of the API) and never a local
    // one, which is exactly when a clear message matters most.
    const text = data.trim();
    if (/^<(!doctype|html|\?xml)/i.test(text)) return `Request failed (${status})`;
    return text;
  }
  const { detail, message, error } = data;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    // Validation errors — surface the first field's message.
    const first = detail[0];
    if (first && typeof first.msg === "string") {
      return cleanMsg(first.msg);
    }
  }
  if (error && typeof error.message === "string") return error.message;
  // api/main.py's own 422 handler answers with
  // {"error": {"type": "VALIDATION_ERROR", "errors": [...]}} — no `detail` and no
  // `message`. Nothing here read `errors`, so EVERY validation failure in the
  // product fell through to the bare "Request failed (422)" below and the user
  // was never told which field was wrong or why.
  if (error && Array.isArray(error.errors) && error.errors.length) {
    const first = error.errors[0];
    const field = fieldOf(first);
    const text = cleanMsg(first && first.msg) || "is not valid";
    return field ? `${field}: ${text}` : text;
  }
  if (typeof message === "string") return message;
  return `Request failed (${status})`;
}

export class ApiError extends Error {
  constructor(status, message, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export function getToken() {
  try {
    // localStorage = "remember me" (survives restart); sessionStorage = this tab
    // only. Read both so either persistence mode authenticates the same way.
    const t = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    // Self-heal a leftover fake "demo." token (from an offline login fallback):
    // it sends a bogus Bearer the real backend rejects, which traps the app in
    // the "backend not reachable" demo banner forever. On a local/dev origin the
    // backend is expected to be real, so drop the demo token instead of using it
    // — the next request runs unauthenticated and the UI prompts a real login.
    if (t && t.startsWith("demo.") && typeof window !== "undefined") {
      const isLocalOrigin =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      if (isLocalOrigin) {
        localStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_KEY);
        // Also drop the stale demo *user* object — otherwise the app still
        // thinks a session exists, routes past the login form, and the app
        // keeps calling the API unauthenticated (403 "Not authenticated").
        try { localStorage.removeItem("aiqa-user"); } catch (e) { /* ignore */ }
        return null;
      }
    }
    return t;
  } catch (e) {
    return null;
  }
}

export function setToken(token, { session = false } = {}) {
  try {
    // Clear both stores first so switching persistence never leaves a stale copy.
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    if (token) (session ? sessionStorage : localStorage).setItem(TOKEN_KEY, token);
  } catch (e) {
    // The sign-in itself succeeded; only persisting it failed. The user stays
    // logged in for this tab and is signed out again on reload, with no error
    // anywhere — which is precisely the report that is impossible to diagnose.
    ignored("persisting the auth token", e);
  }
}

async function request(path, { method = "GET", body, params, signal, timeoutMs } = {}) {
  // Resolve against the page origin so a relative API_BASE ("") works with the
  // dev proxy, while an absolute API_BASE still wins.
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = new URL(getApiBase() + path, origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    });
  }

  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  // Hard ceiling so a hung backend/socket can't block the caller (and the extension's batch
  // loop) forever. Combine with any caller-supplied AbortSignal (e.g. a run's cancel signal),
  // and let a caller pass timeoutMs:0 to opt out for a genuinely long request.
  const ms = timeoutMs === undefined ? 120000 : timeoutMs;
  const ctl = new AbortController();
  let timer = null;
  if (ms) timer = setTimeout(() => { try { ctl.abort(); } catch (e) { /* ignore */ } }, ms);
  if (signal) {
    if (signal.aborted) { try { ctl.abort(); } catch (e) { /* ignore */ } }
    else signal.addEventListener("abort", () => { try { ctl.abort(); } catch (e) { /* ignore */ } }, { once: true });
  }

  // NB: never log `body` (may hold passwords) or `headers` (holds the Bearer token).
  // eslint-disable-next-line no-console -- dev-only: DEBUG folds to false in a production build and terser drops the call (verified absent from the bundle).
  DEBUG && console.log(`[api] ${method} ${url.toString()}`);
  let res;
  try {
    res = await fetch(url.toString(), {
      method,
      headers,
      body: body !== undefined && body !== null ? JSON.stringify(body) : undefined,
      signal: ctl.signal,
    });
    // eslint-disable-next-line no-console -- dev-only: DEBUG folds to false in a production build and terser drops the call (verified absent from the bundle).
    DEBUG && console.log(`[api] ${method} ${url.toString()} → ${res.status}`);
  } catch (e) {
    if (e && e.name === "AbortError") {
      DEBUG && console.error(`[api] ABORTED/TIMEOUT ${method} ${url.toString()}`);
      throw new ApiError(0, "The request was cancelled or timed out.", null);
    }
    DEBUG && console.error(`[api] NETWORK ERROR ${method} ${url.toString()}`, e.message);
    throw new ApiError(0, "Cannot reach the server. Is the backend running?", null);
  } finally {
    if (timer) clearTimeout(timer);
  }

  // A 401 on a request we sent a token with means the stored session is dead —
  // drop it everywhere and show the "expired" message. A 401 on an UNauthenticated
  // request (e.g. the login endpoint itself rejecting bad credentials) is not a
  // session problem: let the backend's real message ("Invalid email or password")
  // flow through the normal error path below instead of hijacking it.
  if (res.status === 401 && token) {
    setToken(null);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("aiqa:unauthorized"));
    }
    throw new ApiError(401, "Your session expired. Please sign in again.");
  }

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      data = text;
    }
  }

  if (res.status === 401) {
    // A 401 only means "session expired" when we actually SENT a token that the
    // backend then rejected. A 401 on an unauthenticated request (e.g. a failed
    // login with wrong/nonexistent credentials) is NOT an expired session —
    // surface the backend's real message ("Invalid email or password") instead
    // of clobbering it and bogusly dropping a session that never existed.
    if (token) {
      setToken(null);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("aiqa:unauthorized"));
      }
      throw new ApiError(401, "Your session expired. Please sign in again.", data);
    }
    const message =
      (data && (data.detail || data.message)) || "Invalid email or password";
    throw new ApiError(401, message, data);
  }

  if (!res.ok) {
    throw new ApiError(res.status, extractErrorMessage(data, res.status), data);
  }

  // A non-JSON 200 means the request didn't reach the API (e.g. fell through to
  // index.html). Treat it as an error so the UI falls back instead of crashing.
  if (typeof data === "string") {
    throw new ApiError(
      res.status,
      "Expected JSON but got a non-JSON response (check the API base / dev proxy)."
    );
  }

  return data;
}

/** Build a query string, dropping empty values.
 *
 * Server-side filtering means a cleared filter must VANISH from the URL — sending
 * `?status=` would otherwise be read as "match the empty status" and return nothing.
 */
export function qs(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.set(k, v);
  });
  const out = q.toString();
  return out ? `?${out}` : "";
}

export const api = {
  get: (path, opts) => request(path, { ...opts, method: "GET" }),
  post: (path, body, opts) => request(path, { ...opts, method: "POST", body }),
  put: (path, body, opts) => request(path, { ...opts, method: "PUT", body }),
  patch: (path, body, opts) => request(path, { ...opts, method: "PATCH", body }),
  // DELETE carries a body for the actions that re-authenticate (removing a team
  // member asks for the caller's password). Callers that don't need one just
  // omit it and nothing is sent.
  del: (path, body, opts) =>
    request(path, { ...opts, method: "DELETE", ...(body !== undefined ? { body } : {}) }),
};

// Fetch a binary asset (e.g. a screenshot) from an auth-protected endpoint and
// return an object URL. A plain <img src> can't send the Bearer header, so we
// fetch with auth and hand back a blob URL. Returns null on any failure.
export async function authedBlobUrl(path) {
  try {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const url = new URL(getApiBase() + path, origin);
    const token = getToken();
    const res = await fetch(url.toString(), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch (e) {
    return null;
  }
}

// The filename the server chose for a download, out of its Content-Disposition.
//
// Two things the naive /filename="?([^"]+)"?/ this replaces got wrong:
//
//   1. It ignored `filename*`. The server sends RFC 6266 for any non-ASCII name —
//      an ASCII fallback in `filename=` PLUS the real, percent-encoded name in
//      `filename*=UTF-8''…` — so a project called "テスト" downloaded under the
//      stripped-down fallback even though its real name was right there.
//   2. Worse, on a header that has ONLY `filename*`, `filename="?([^"]+)"?`
//      happily matched the star form and produced a literal
//      `*=UTF-8''%E3%83%86…` as the filename.
//
// `filename*` wins when both are present, which is what RFC 6266 requires.
//
// NB this only works cross-origin because the API sets
// `Access-Control-Expose-Headers: Content-Disposition` — without it the browser
// hides the header from JS entirely and every download falls back to `fallback`.
export function filenameFromDisposition(header, fallback) {
  const value = header || "";

  // RFC 5987 ext-value: filename*=UTF-8''<percent-encoded>  (charset is
  // case-insensitive and the language segment between the quotes is optional).
  const starred = value.match(/filename\*\s*=\s*([^']*)'([^']*)'([^;]+)/i);
  if (starred) {
    try {
      const decoded = decodeURIComponent(starred[3].trim());
      if (decoded) return decoded;
    } catch (e) {
      /* malformed percent-encoding — fall through to the plain form */
    }
  }

  // Plain form, quoted or bare. The negative lookahead keeps this from matching
  // the `filename*=` above when only that one is present.
  const plain = value.match(/filename\s*=\s*(?!\*)"([^"]*)"|filename\s*=\s*(?!\*)([^;]+)/i);
  const name = plain && (plain[1] ?? plain[2]);
  return (name && name.trim()) || fallback;
}
